import { CheckOutlined, CloseOutlined } from "@ant-design/icons"
import { Button, Flex, Form, Select } from "antd"
import { type SelectProps } from "antd/es/select"
import { type BaseOptionType } from "antd/lib/select"
import React, { type ReactNode } from "react"

import {
  type CellEditors,
  buildEditRowCells,
  rowCommitKeyDown
} from "../../../../../components/EntityTable/editable-row-cells.ts"
import { useRowEscapeCancel } from "../../../../../components/EntityTable/use-row-escape-cancel.ts"
import InputNumber from "../../../../../components/Input/InputNumber.tsx"
import { ResolvedSelect } from "../../../../../components/Select/ResolvedSelect.tsx"
import {
  UNRESOLVED_USER_LABEL,
  buildEntityLabelMap
} from "../../../../../utils/select/entity-labels.ts"
import { pickableOptions } from "../../../../../utils/select/managed-list-options.ts"
import {
  COMMON_REQUIRED_FIELDS,
  TERMINAL_STATUSES
} from "../../../../Lead/constants/lead-required-fileds.ts"
import type { LeadStatusesValues } from "../../../../Lead/constants/lead-statuses.ts"
import type { LeadSalesManager } from "../../../../Lead/types/lead.types.ts"
import {
  LEAD_SOURCE_NONE,
  withNoneSourceOption
} from "../../../constants/lead-source-none.ts"
import { type LeadListItem } from "../../../types/types.ts"
import { LEADS_COLUMN_KEYS } from "./columns.tsx"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"

// The row is seeded from a list item, but its status editor works in status
// *values* while the list item carries the display label the API sends.
export type LeadEditableData = Omit<LeadListItem, "status"> & {
  status?: number
}

type RequiredFieldsSchema = Partial<{
  [key in keyof LeadListItem]: boolean
}>

interface LeadsListEditableRowProps {
  children: ReactNode[]
  editingID: string
  filterOptions: Record<string, BaseOptionType[]>
  record: LeadListItem
  assignedSalesAllowed?: boolean
  onSelectRow: (record: LeadListItem, actions: string) => void
  /** May arrive via the table's `onRow`; composed with the row's own handler. */
  onKeyDown?: React.KeyboardEventHandler<HTMLTableRowElement>
}

export const LeadsListEditableRow = ({
  children,
  editingID,
  filterOptions,
  record,
  onSelectRow,
  assignedSalesAllowed,
  ...rest
}: LeadsListEditableRowProps) => {
  const isEditing = record?.id === editingID
  const cancelEdit = () => onSelectRow(record, "cancel")
  // Escape leaves the row the way its ✕ does, from the moment it opens. Called
  // before the early return so every row keeps the same hooks; only the open
  // row listens.
  useRowEscapeCancel(cancelEdit, !isEditing)

  if (!isEditing) return <tr {...rest}>{children}</tr>
  const status = filterOptions?.["status"]?.find(
    (s) => s?.label === record?.status
  )?.value

  const requiredFields: RequiredFieldsSchema = (() => {
    if (!status || TERMINAL_STATUSES.includes(+status as LeadStatusesValues)) {
      return {} as RequiredFieldsSchema
    }

    return {
      sales_manager: true
    }
  })()

  const editors: CellEditors = {
    [LEADS_COLUMN_KEYS.leadName]: (
      <td className="ant-table-cell">
        <Form.Item<LeadListItem>
          className={"px-0 m-0"}
          name={"lead_name"}
          rules={[
            {
              required: COMMON_REQUIRED_FIELDS.lead_name,
              whitespace: true,
              message: "Name is required"
            }
          ]}
        >
          <TextInput placeholder={"Enter name"} />
        </Form.Item>
      </td>
    ),
    [LEADS_COLUMN_KEYS.source]: (
      <td className="ant-table-cell">
        <Form.Item<LeadEditableData>
          className={"px-0 m-0"}
          name={"source"}
          rules={[
            {
              required: COMMON_REQUIRED_FIELDS.source,
              message: "Source is required"
            }
          ]}
          normalize={(value: string) => value ?? LEAD_SOURCE_NONE}
        >
          <Select
            showSearch
            allowClear
            options={pickableOptions(
              withNoneSourceOption(filterOptions?.["source"]),
              record.source
            )}
            className={"w-full"}
            placeholder={"Select source"}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input?.toLowerCase())
            }
          />
        </Form.Item>
      </td>
    ),
    [LEADS_COLUMN_KEYS.salesPrice]: (
      <td className="ant-table-cell">
        <Flex vertical>
          <Form.Item<LeadEditableData>
            name={"sales_price"}
            className={"px-0 m-0"}
            getValueFromEvent={(value) => value?.toFixed(2) ?? ""}
          >
            <InputNumber
              prefix="$"
              placeholder="0.00"
              step={0.01}
              max={999999999.99}
              className={"w-full"}
            />
          </Form.Item>
        </Flex>
      </td>
    ),
    ...(assignedSalesAllowed
      ? {
          [LEADS_COLUMN_KEYS.salesManager]: (
            <td className="ant-table-cell">
              <Form.Item<LeadEditableData>
                className={"px-0 m-0"}
                name={"sales_manager"}
                rules={[
                  {
                    required: !!requiredFields?.sales_manager,
                    message: "Sales manager is required"
                  }
                ]}
              >
                <SalesSelect
                  record={record}
                  mode={"multiple"}
                  options={filterOptions?.["sales_manager"] || []}
                  className={"w-full"}
                  placeholder={"Select sales manager"}
                  disabled={!assignedSalesAllowed}
                  filterOption={(input, option) =>
                    ((option?.label as string) ?? "")
                      .toLowerCase()
                      .includes(input?.toLowerCase())
                  }
                />
              </Form.Item>
            </td>
          )
        }
      : {})
  }

  const onKeyDownRow = rowCommitKeyDown(() => onSelectRow(record, "save_row"), {
    incoming: rest.onKeyDown,
    cancel: cancelEdit
  })

  const actionsCell = (
    <td
      className={
        "sticky right-0 ant-table-cell ant-table-cell-fix-right ant-table-cell-fix-right-first"
      }
    >
      <Flex gap={"small"} justify={"center"} className={"mx-auto"}>
        <Button
          variant={"outlined"}
          color={"danger"}
          onClick={cancelEdit}
          icon={<CloseOutlined />}
        />

        <Button
          variant={"outlined"}
          color={"green"}
          onClick={() => onSelectRow(record, "save_row")}
          icon={<CheckOutlined />}
        />
      </Flex>
    </td>
  )

  return (
    <tr {...rest} onKeyDown={onKeyDownRow}>
      {buildEditRowCells(
        children,
        Object.values(LEADS_COLUMN_KEYS),
        editors,
        actionsCell
      )}
    </tr>
  )
}

interface SalesSelectProps extends SelectProps {
  value?: LeadSalesManager[] | null
  record: LeadListItem
}

const SalesSelect = ({
  value,
  onChange,
  record,
  ...props
}: SalesSelectProps) => {
  const primitiveValue = value?.map((v) => v?.id)

  const onChangeHandler = (ids: string[]) => {
    const sales = ids?.map((id) => {
      const existing = record?.sales_manager?.find((s) => s?.id === id)

      if (existing) return { id, is_main: existing?.is_main }
      return { id, is_main: false }
    })

    onChange?.(sales)
  }

  return (
    <ResolvedSelect
      value={primitiveValue}
      onChange={onChangeHandler}
      fallbackLabels={buildEntityLabelMap(record?.sales_manager)}
      fallbackLabel={UNRESOLVED_USER_LABEL}
      {...props}
    />
  )
}
